import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { 
  Target, 
  Brain, 
  Lightbulb, 
  Users, 
  MapPin, 
  Briefcase,
  Search,
  Zap,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { targetCriteriaSchema, TargetCriteriaFormData } from '../../../schemas/onboarding';
import { 
  COMPANY_SIZES, 
  LOCATIONS, 
  INDUSTRIES, 
  JOB_TITLES, 
  DEPARTMENTS, 
  PAIN_POINTS, 
  BUYING_SIGNALS,
  TARGETING_SUGGESTIONS 
} from '../../../constants/onboarding';
import { MultiSelect } from '../MultiSelect';
import { RichTextEditor } from '../RichTextEditor';
import { CompanyInformationFormData } from '../../../schemas/onboarding';

interface TargetingStepProps {
  data: Partial<TargetCriteriaFormData>;
  companyData: CompanyInformationFormData;
  onNext: (data: TargetCriteriaFormData) => void;
  onBack: () => void;
  className?: string;
  onStepComplete?: (isComplete: boolean) => void;
}

export const TargetingStep: React.FC<TargetingStepProps> = ({
  data,
  companyData,
  onNext,
  onBack,
  className = '',
  onStepComplete,
}) => {
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<TargetCriteriaFormData>({
    resolver: zodResolver(targetCriteriaSchema),
    defaultValues: {
      companySize: data.companySize || [],
      location: data.location || [],
      industries: data.industries || [],
      jobTitles: data.jobTitles || [],
      departments: data.departments || [],
      painPoints: data.painPoints || [],
      buyingSignals: data.buyingSignals || [],
      targetDescription: data.targetDescription || '',
    },
    mode: 'onChange',
  });

  const watchedDescription = watch('targetDescription');

  // Track step completion based on basic criteria
  useEffect(() => {
    const hasDescription = !!watchedDescription && watchedDescription.length >= 20;
    onStepComplete?.(hasDescription);
  }, [watchedDescription, onStepComplete]);

  // Convert constants to options
  const companySizeOptions = COMPANY_SIZES.map(size => ({ value: size, label: size }));
  const locationOptions = LOCATIONS.map(location => ({ value: location, label: location }));
  const industryOptions = INDUSTRIES.map(industry => ({ value: industry, label: industry }));
  const jobTitleOptions = JOB_TITLES.map(title => ({ value: title, label: title }));
  const departmentOptions = DEPARTMENTS.map(dept => ({ value: dept, label: dept }));
  const painPointOptions = PAIN_POINTS.map(point => ({ value: point, label: point }));
  const buyingSignalOptions = BUYING_SIGNALS.map(signal => ({ value: signal, label: signal }));

  // Simulate AI processing of natural language description
  const processDescription = async (description: string) => {
    if (!description || description.length < 20) return;

    setIsProcessingDescription(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock AI extraction based on keywords
    const mockExtraction = {
      extractedCriteria: {
        industries: extractKeywords(description, INDUSTRIES),
        jobTitles: extractKeywords(description, JOB_TITLES),
        companySize: extractKeywords(description, COMPANY_SIZES),
        painPoints: extractKeywords(description, PAIN_POINTS),
        buyingSignals: extractKeywords(description, BUYING_SIGNALS),
      },
      suggestions: {
        improvedDescription: generateImprovedDescription(description),
        additionalTargets: generateAdditionalTargets(description),
        optimizationTips: generateOptimizationTips(description),
      },
      confidence: Math.random() * 0.3 + 0.7, // 70-100%
    };

    setAiSuggestions(mockExtraction);
    setIsProcessingDescription(false);

    // Auto-fill suggestions if user wants
    applyAISuggestions(mockExtraction);
  };

  const extractKeywords = (text: string, keywords: string[]) => {
    const lowercaseText = text.toLowerCase();
    return keywords.filter(keyword => 
      lowercaseText.includes(keyword.toLowerCase())
    ).slice(0, 3);
  };

  const generateImprovedDescription = (description: string) => {
    return `${description} Focus on companies showing growth signals, actively hiring, or recently funded. Look for decision-makers who have budget authority and urgency around solving this problem.`;
  };

  const generateAdditionalTargets = (description: string) => {
    return [
      'Companies with 50-500 employees in expansion phase',
      'Organizations recently posting relevant job openings',
      'Businesses attending industry conferences this quarter',
    ];
  };

  const generateOptimizationTips = (description: string) => {
    const tips = [];
    if (!description.includes('size')) {
      tips.push('Consider adding company size criteria for better targeting');
    }
    if (!description.includes('location')) {
      tips.push('Specify geographic regions to focus your outreach');
    }
    if (description.length < 50) {
      tips.push('Add more detail for more accurate AI processing');
    }
    return tips;
  };

  const applyAISuggestions = (suggestions: any) => {
    // Auto-apply high-confidence suggestions
    if (suggestions.confidence > 0.8) {
      if (suggestions.extractedCriteria.industries.length > 0) {
        const currentIndustries = watch('industries') || [];
        const newIndustries = Array.from(new Set([...currentIndustries, ...suggestions.extractedCriteria.industries]));
        setValue('industries', newIndustries);
      }
      if (suggestions.extractedCriteria.jobTitles.length > 0) {
        const currentJobTitles = watch('jobTitles') || [];
        const newJobTitles = Array.from(new Set([...currentJobTitles, ...suggestions.extractedCriteria.jobTitles]));
        setValue('jobTitles', newJobTitles);
      }
      if (suggestions.extractedCriteria.companySize.length > 0) {
        const currentCompanySize = watch('companySize') || [];
        const newCompanySize = Array.from(new Set([...currentCompanySize, ...suggestions.extractedCriteria.companySize]));
        setValue('companySize', newCompanySize);
      }
      if (suggestions.extractedCriteria.painPoints.length > 0) {
        const currentPainPoints = watch('painPoints') || [];
        const newPainPoints = Array.from(new Set([...currentPainPoints, ...suggestions.extractedCriteria.painPoints]));
        setValue('painPoints', newPainPoints);
      }
    }
  };

  // Process description when user stops typing
  React.useEffect(() => {
    if (watchedDescription && watchedDescription.length > 20) {
      const timer = setTimeout(() => {
        processDescription(watchedDescription);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [watchedDescription]);

  const onSubmit = (formData: TargetCriteriaFormData) => {
    onNext(formData);
  };

  const handleContinueWithMinimalData = () => {
    const currentData = watch();
    // Convert to full TargetCriteriaFormData with minimal requirements
    const minimalData: TargetCriteriaFormData = {
      companySize: currentData.companySize?.length ? currentData.companySize : ['Medium Business (51-200 employees)'],
      location: currentData.location?.length ? currentData.location : ['North America'],
      industries: currentData.industries?.length ? currentData.industries : companyData.industries || ['Technology'],
      jobTitles: currentData.jobTitles?.length ? currentData.jobTitles : ['CEO', 'Founder'],
      departments: currentData.departments?.length ? currentData.departments : ['Executive Leadership'],
      painPoints: currentData.painPoints?.length ? currentData.painPoints : ['Struggling to generate qualified leads'],
      buyingSignals: currentData.buyingSignals?.length ? currentData.buyingSignals : ['Rapid company growth'],
      targetDescription: currentData.targetDescription || 'General business prospects in our industry',
    };
    onNext(minimalData);
  };

  return (
    <div className={`max-w-4xl mx-auto px-6 py-8 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-secondary-50 rounded-full mb-4">
            <Target className="w-6 h-6 text-secondary-500" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-900 mb-2">
            Define Your Ideal Customers
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Describe your perfect prospects in natural language. Our AI will analyze and suggest targeting criteria.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Natural Language Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-6 h-6 text-primary-500" />
              <h3 className="text-xl font-semibold text-neutral-900">
                Describe Your Target Audience
              </h3>
              {isProcessingDescription && (
                <div className="flex items-center gap-2 text-sm text-primary-600">
                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span>AI is analyzing...</span>
                </div>
              )}
            </div>
            
            <Controller
              name="targetDescription"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Example: I want to target SaaS companies with 50-200 employees that are growing rapidly and likely struggling with customer support at scale. Looking for founders, CTOs, or heads of customer success who would value automation tools..."
                  maxLength={1000}
                  error={errors.targetDescription?.message}
                  showOptimizationTips={true}
                />
              )}
            />

            {/* AI Suggestions */}
            {aiSuggestions && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-white rounded-xl border border-primary-200"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-warning-500" />
                  <span className="font-semibold text-neutral-900">AI Analysis Complete</span>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    aiSuggestions.confidence > 0.8 ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                  }`}>
                    {Math.round(aiSuggestions.confidence * 100)}% confidence
                  </span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Extracted Keywords</h4>
                    <div className="space-y-2 text-sm">
                      {aiSuggestions.extractedCriteria.industries.length > 0 && (
                        <div>
                          <span className="text-neutral-600">Industries:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {aiSuggestions.extractedCriteria.industries.map((industry: string) => (
                              <span key={industry} className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                                {industry}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiSuggestions.extractedCriteria.jobTitles.length > 0 && (
                        <div>
                          <span className="text-neutral-600">Job Titles:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {aiSuggestions.extractedCriteria.jobTitles.map((title: string) => (
                              <span key={title} className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded text-xs">
                                {title}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Optimization Tips</h4>
                    <div className="space-y-1">
                      {aiSuggestions.suggestions.optimizationTips.map((tip: string, index: number) => (
                        <div key={index} className="flex items-start gap-2 text-sm text-neutral-600">
                          <CheckCircle2 className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Advanced Targeting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium mb-4"
            >
              <Search className="w-4 h-4" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Targeting Options
            </button>

            {showAdvanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="grid md:grid-cols-2 gap-6 bg-white rounded-2xl p-6 border border-neutral-200"
              >
                {/* Company Size */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Company Size *
                  </label>
                  <Controller
                    name="companySize"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={companySizeOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select company sizes"
                        suggestions={['Medium Business (51-200 employees)', 'Large Enterprise (201-1000 employees)']}
                        error={errors.companySize?.message}
                      />
                    )}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Location *
                  </label>
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={locationOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select locations"
                        suggestions={['North America', 'United States']}
                        error={errors.location?.message}
                      />
                    )}
                  />
                </div>

                {/* Industries */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Industries *
                  </label>
                  <Controller
                    name="industries"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={industryOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select industries"
                        suggestions={companyData.industries || []}
                        error={errors.industries?.message}
                      />
                    )}
                  />
                </div>

                {/* Job Titles */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Job Titles *
                  </label>
                  <Controller
                    name="jobTitles"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={jobTitleOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select job titles"
                        allowCustom={true}
                        suggestions={['CEO', 'Founder', 'VP Sales']}
                        error={errors.jobTitles?.message}
                      />
                    )}
                  />
                </div>

                {/* Departments */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Departments *
                  </label>
                  <Controller
                    name="departments"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={departmentOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select departments"
                        suggestions={['Sales', 'Marketing', 'Executive Leadership']}
                        error={errors.departments?.message}
                      />
                    )}
                  />
                </div>

                {/* Pain Points */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Pain Points *
                  </label>
                  <Controller
                    name="painPoints"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={painPointOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select pain points"
                        allowCustom={true}
                        error={errors.painPoints?.message}
                      />
                    )}
                  />
                </div>

                {/* Buying Signals */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Buying Signals *
                  </label>
                  <Controller
                    name="buyingSignals"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        options={buyingSignalOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select buying signals"
                        suggestions={['Recently raised funding', 'Rapid company growth', 'New leadership hiring']}
                        error={errors.buyingSignals?.message}
                      />
                    )}
                  />
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="flex items-center justify-between pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
            >
              Back
            </button>
            
            <div className="flex items-center gap-3">
              {/* Continue with current data if description is provided */}
              {watchedDescription && watchedDescription.length >= 20 && !isValid && (
                <button
                  type="button"
                  onClick={handleContinueWithMinimalData}
                  className="px-6 py-3 border border-primary-300 text-primary-700 hover:text-primary-900 hover:border-primary-400 rounded-xl font-medium transition-all duration-200"
                >
                  Continue with AI Suggestions
                </button>
              )}
              
              <button
                type="submit"
                disabled={!isValid && !(watchedDescription && watchedDescription.length >= 20)}
                className={`
                  px-8 py-3 rounded-xl font-semibold transition-all duration-200
                  ${isValid
                    ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-glow-primary hover:shadow-strong'
                    : (watchedDescription && watchedDescription.length >= 20)
                      ? 'bg-secondary-500 hover:bg-secondary-600 text-white shadow-glow-secondary hover:shadow-strong'
                      : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                  }
                `}
                onClick={isValid ? undefined : handleContinueWithMinimalData}
              >
                {isValid ? 'Continue to Goals' : 'Continue to Goals'}
              </button>
            </div>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}; 